import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronUp } from "lucide-react";
import { BRAND } from "../data/content";

// Routes where the floating Back-to-Top button is distracting and we hide it.
const HIDDEN_ROUTES = ["/cart", "/checkout"];

/**
 * Back to Top floating button — BTW energy, F3 mascot in the center.
 * Appears after scrolling 200px. Smooth-scrolls to the top.
 * Hidden during the checkout flow (`/cart`, `/checkout/*`).
 */
export const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  const onHiddenRoute = HIDDEN_ROUTES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (onHiddenRoute) return null;

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      data-testid="back-to-top-btn"
      aria-label="Back to top"
      onClick={handleClick}
      className={`group fixed right-4 sm:right-5 z-50 flex flex-col items-center gap-1 sm:gap-1.5 transition-all duration-300 ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      style={{
        // Sit above the sticky mobile CTA on phones/tablets; pin to bottom on desktop.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + var(--back-to-top-bottom))",
      }}
    >
      <div className="relative w-11 h-11 sm:w-16 sm:h-16">
        {/* Halo glow behind mascot */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[#5FBDD6]/35"
          style={{ animation: "btwHalo 2.4s ease-in-out infinite" }}
        />
        {/* Main button face */}
        <div
          className="relative w-full h-full rounded-full bg-[#0B0B0B] border-2 border-[#5FBDD6] flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-1"
          style={{ animation: "btwPulse 2.2s ease-in-out infinite" }}
        >
          <img
            src={BRAND.logo}
            alt=""
            className="w-5 h-5 sm:w-8 sm:h-8 object-contain select-none pointer-events-none"
            draggable="false"
          />
        </div>
        {/* Lime badge with up arrow */}
        <span
          aria-hidden="true"
          className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 w-4 sm:h-[22px] sm:w-[22px] rounded-full bg-[#A3D900] text-[#0B0B0B] shadow-[0_4px_10px_rgba(163,217,0,0.45)] ring-2 ring-[#0B0B0B]"
        >
          <ChevronUp className="h-2.5 w-2.5 sm:h-[14px] sm:w-[14px]" strokeWidth={3} />
        </span>
      </div>
      {/* Hide the "TOP" caption on mobile to reduce the button's footprint;
          show on sm+ where there's room without crowding form fields. */}
      <span className="hidden sm:inline text-[11px] font-extrabold uppercase tracking-[0.22em] text-brand-charcoal group-hover:text-[#5FBDD6] transition-colors duration-200 drop-shadow-[0_1px_0_rgba(255,255,255,0.8)]">
        Top
      </span>
    </button>
  );
};

export default BackToTop;
